import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface EditableDateFieldProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
}

export const EditableDateField: React.FC<EditableDateFieldProps> = ({
  value,
  onChange,
  placeholder = 'Click to select date',
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  if (isEditing) {
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        <DatePicker
          selected={tempValue}
          onChange={(date) => {
            setTempValue(date);
            if (date) {
              handleSave();
            }
          }}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancel();
          }}
          className="border border-blue-500 ring-2 ring-blue-200 rounded px-2 py-1 focus:outline-none"
          autoFocus
          showPopperArrow={false}
        />
      </div>
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className={`cursor-pointer hover:bg-gray-50 rounded px-2 py-1 min-h-[2rem] ${
        !value ? 'text-gray-400' : ''
      } ${className}`}
    >
      {formatDate(value) || placeholder}
    </div>
  );
};
